<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useFunctionGetter, useMapGetter } from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';
import AccordionItem from 'dashboard/components/Accordion/AccordionItem.vue';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { emitter } from 'shared/helpers/mitt';

import groupsService from '/plugins/quepasa-inbox/src/services/groups.js';
import { isQuePasaInbox } from '/plugins/quepasa-inbox/src/helpers/inboxHelpers.js';
import {
  buildGroupExportRows,
  isGroupIdentifier,
  normalizeGroupParticipant,
  normalizePhoneValue,
} from '/plugins/quepasa-inbox/src/helpers/groupHelpers.js';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    default: null,
  },
  inboxId: {
    type: Number,
    default: null,
  },
});

const { t } = useI18n();
const { uiSettings, updateUISettings } = useUISettings();
const currentAccountId = useMapGetter('getCurrentAccountId');
const currentChat = useMapGetter('getSelectedChat');
const inbox = useFunctionGetter(
  'inboxes/getInbox',
  computed(() => props.inboxId || currentChat.value?.inbox_id)
);
const currentContact = useFunctionGetter(
  'contacts/getContact',
  computed(() => currentChat.value?.meta?.sender?.id)
);

const loading = ref(false);
const inviteLoading = ref(false);
const savingParticipants = ref(false);
const importingParticipants = ref(false);
const error = ref('');
const group = ref(null);
const participants = ref([]);
const participantInput = ref('');
const search = ref('');

const resolvedInboxId = computed(
  () => props.inboxId || currentChat.value?.inbox_id
);
const resolvedConversationId = computed(
  () => props.conversationId || currentChat.value?.id
);
const groupIdentifier = computed(() => {
  return (
    currentContact.value?.identifier ||
    currentChat.value?.meta?.sender?.identifier ||
    ''
  );
});

const isVisible = computed(() => {
  return (
    isQuePasaInbox(inbox.value) && isGroupIdentifier(groupIdentifier.value)
  );
});

const normalizedParticipants = computed(() => {
  return (participants.value || []).map(participant =>
    normalizeGroupParticipant(participant)
  );
});

const filteredParticipants = computed(() => {
  const query = search.value.trim().toLowerCase();

  if (!query) {
    return normalizedParticipants.value;
  }

  return normalizedParticipants.value.filter(participant => {
    return (
      participant.displayName.toLowerCase().includes(query) ||
      participant.phone.includes(query) ||
      participant.identifier.toLowerCase().includes(query)
    );
  });
});

const mentionableParticipantsCount = computed(() => {
  return normalizedParticipants.value.filter(
    participant => participant.mentionable
  ).length;
});

const participantSummary = computed(() => {
  return t('QUEPASA_INBOX.GROUPS.PANEL.SUMMARY', {
    count: normalizedParticipants.value.length,
    mentionable: mentionableParticipantsCount.value,
  });
});

const isAccordionOpen = computed(
  () => uiSettings.value?.is_quepasa_group_open ?? true
);

const toggleAccordion = () => {
  updateUISettings({
    is_quepasa_group_open: !isAccordionOpen.value,
  });
};

const loadGroup = async (force = false) => {
  if (
    !isVisible.value ||
    !currentAccountId.value ||
    !resolvedInboxId.value ||
    !resolvedConversationId.value
  ) {
    group.value = null;
    participants.value = [];
    return;
  }

  loading.value = true;
  error.value = '';

  try {
    const response = await groupsService.details(
      currentAccountId.value,
      resolvedInboxId.value,
      groupIdentifier.value,
      { force }
    );

    group.value = response.group || null;
    participants.value =
      response.participants || response.group?.Participants || [];
  } catch (requestError) {
    error.value = requestError.response?.data?.error || requestError.message;
  } finally {
    loading.value = false;
  }
};

const copyText = async text => {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const input = document.createElement('textarea');
  input.value = text;
  document.body.appendChild(input);
  input.select();
  document.execCommand('copy');
  document.body.removeChild(input);
};

const copyInviteLink = async () => {
  if (!currentAccountId.value || !resolvedInboxId.value) return;

  inviteLoading.value = true;

  try {
    const response = await groupsService.invite(
      currentAccountId.value,
      resolvedInboxId.value,
      groupIdentifier.value
    );
    await copyText(response.url);
    useAlert(t('QUEPASA_INBOX.GROUPS.PANEL.INVITE_COPIED'));
  } catch (requestError) {
    useAlert(
      requestError.response?.data?.error ||
        t('QUEPASA_INBOX.GROUPS.PANEL.INVITE_ERROR')
    );
  } finally {
    inviteLoading.value = false;
  }
};

const downloadFile = (content, mimeType, extension) => {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = `quepasa-group-${groupIdentifier.value.replace(/[^a-zA-Z0-9]/g, '_')}.${extension}`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const escapeHtml = value => {
  return String(value || '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;');
};

const exportParticipants = format => {
  const rows = buildGroupExportRows(normalizedParticipants.value);

  if (format === 'excel') {
    const html = `\ufeff
      <html>
        <head>
          <meta charset="utf-8" />
        </head>
        <body>
          <table>
            <thead>
              <tr>
                <th>${escapeHtml(t('QUEPASA_INBOX.GROUPS.PANEL.COLUMNS.NAME'))}</th>
                <th>${escapeHtml(t('QUEPASA_INBOX.GROUPS.PANEL.COLUMNS.PHONE'))}</th>
                <th>${escapeHtml(t('QUEPASA_INBOX.GROUPS.PANEL.COLUMNS.IDENTIFIER'))}</th>
                <th>${escapeHtml(t('QUEPASA_INBOX.GROUPS.PANEL.COLUMNS.ROLE'))}</th>
              </tr>
            </thead>
            <tbody>
              ${rows
                .map(
                  row => `
                    <tr>
                      <td>${escapeHtml(row.name)}</td>
                      <td>${escapeHtml(row.phone)}</td>
                      <td>${escapeHtml(row.identifier)}</td>
                      <td>${escapeHtml(row.admin)}</td>
                    </tr>
                  `
                )
                .join('')}
            </tbody>
          </table>
        </body>
      </html>`;

    downloadFile(html, 'application/vnd.ms-excel;charset=utf-8', 'xls');
    return;
  }

  const csv = [
    'Name,Phone,Identifier,Role',
    ...rows.map(row =>
      [row.name, row.phone, row.identifier, row.admin]
        .map(value => `"${String(value || '').replaceAll('"', '""')}"`)
        .join(',')
    ),
  ].join('\n');

  downloadFile(csv, 'text/csv;charset=utf-8', 'csv');
};

const mentionParticipant = participant => {
  if (!participant.mentionable) return;

  emitter.emit(
    BUS_EVENTS.INSERT_INTO_RICH_EDITOR,
    `${participant.mentionToken} `
  );
};

const addParticipants = async () => {
  if (!currentAccountId.value || !resolvedInboxId.value) return;

  const participantPhones = participantInput.value
    .split(/[\n,;]+/)
    .map(value => normalizePhoneValue(value))
    .filter(Boolean);

  if (!participantPhones.length) {
    useAlert(t('QUEPASA_INBOX.GROUPS.PANEL.ADD_EMPTY_ERROR'));
    return;
  }

  savingParticipants.value = true;

  try {
    await groupsService.updateParticipants(
      currentAccountId.value,
      resolvedInboxId.value,
      groupIdentifier.value,
      participantPhones,
      'add'
    );

    participantInput.value = '';
    useAlert(t('QUEPASA_INBOX.GROUPS.PANEL.ADD_SUCCESS'));
    await loadGroup(true);
  } catch (requestError) {
    useAlert(
      requestError.response?.data?.error ||
        t('QUEPASA_INBOX.GROUPS.PANEL.ADD_ERROR')
    );
  } finally {
    savingParticipants.value = false;
  }
};

const importParticipants = async () => {
  if (!currentAccountId.value || !resolvedInboxId.value) return;

  importingParticipants.value = true;

  try {
    const response = await groupsService.importContacts(
      currentAccountId.value,
      resolvedInboxId.value,
      groupIdentifier.value
    );

    useAlert(
      t('QUEPASA_INBOX.GROUPS.PANEL.IMPORT_SUCCESS', {
        created: response.created || 0,
        linked: response.linked_existing || 0,
        skipped: response.skipped_existing || 0,
      })
    );
  } catch (requestError) {
    useAlert(
      requestError.response?.data?.error ||
        t('QUEPASA_INBOX.GROUPS.PANEL.IMPORT_ERROR')
    );
  } finally {
    importingParticipants.value = false;
  }
};

watch(
  groupIdentifier,
  () => {
    loadGroup();
  },
  { immediate: true }
);
</script>

<template>
  <div>
    <div v-if="isVisible" class="px-2 pb-3">
      <AccordionItem
        :title="$t('QUEPASA_INBOX.GROUPS.PANEL.TITLE')"
        :is-open="isAccordionOpen"
        compact
        @toggle="toggleAccordion"
      >
        <div class="p-4 bg-n-slate-2 rounded-bl-lg rounded-br-lg">
          <div class="flex items-start justify-between gap-3">
            <p class="text-sm text-n-slate-11 leading-5">
              {{ participantSummary }}
            </p>
            <button
              class="shrink-0 text-xs font-medium text-n-blue-11 hover:text-n-blue-12"
              type="button"
              @click.stop="loadGroup(true)"
            >
              {{ $t('QUEPASA_INBOX.GROUPS.PANEL.REFRESH') }}
            </button>
          </div>

          <div class="mt-4 flex flex-wrap gap-2">
            <button
              class="inline-flex items-center rounded-lg bg-n-slate-12 px-3 py-2 text-xs font-medium text-n-solid-1 disabled:opacity-50"
              type="button"
              :disabled="inviteLoading"
              @click="copyInviteLink"
            >
              {{
                inviteLoading
                  ? $t('QUEPASA_INBOX.GROUPS.PANEL.INVITE_LOADING')
                  : $t('QUEPASA_INBOX.GROUPS.PANEL.INVITE')
              }}
            </button>
            <button
              class="inline-flex items-center rounded-lg border border-n-weak bg-n-alpha-black2 px-3 py-2 text-xs font-medium text-n-slate-12"
              type="button"
              @click="exportParticipants('csv')"
            >
              {{ $t('QUEPASA_INBOX.GROUPS.PANEL.EXPORT_CSV') }}
            </button>
            <button
              class="inline-flex items-center rounded-lg border border-n-weak bg-n-alpha-black2 px-3 py-2 text-xs font-medium text-n-slate-12"
              type="button"
              @click="exportParticipants('excel')"
            >
              {{ $t('QUEPASA_INBOX.GROUPS.PANEL.EXPORT_EXCEL') }}
            </button>
            <button
              class="inline-flex items-center rounded-lg border border-n-weak bg-n-alpha-black2 px-3 py-2 text-xs font-medium text-n-slate-12 disabled:opacity-50"
              type="button"
              :disabled="importingParticipants"
              @click="importParticipants"
            >
              {{
                importingParticipants
                  ? $t('QUEPASA_INBOX.GROUPS.PANEL.IMPORT_LOADING')
                  : $t('QUEPASA_INBOX.GROUPS.PANEL.IMPORT_CONTACTS')
              }}
            </button>
          </div>

          <div class="mt-4">
            <label class="block text-xs font-medium text-n-slate-11 mb-2">
              {{ $t('QUEPASA_INBOX.GROUPS.PANEL.ADD_LABEL') }}
            </label>
            <textarea
              v-model="participantInput"
              rows="2"
              class="w-full rounded-xl border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12"
              :placeholder="$t('QUEPASA_INBOX.GROUPS.PANEL.ADD_PLACEHOLDER')"
            />
            <div class="mt-3 flex items-center justify-between gap-3">
              <p class="text-xs text-n-slate-11 max-w-[14rem] leading-5">
                {{ $t('QUEPASA_INBOX.GROUPS.PANEL.ADD_HELP') }}
              </p>
              <button
                class="inline-flex items-center rounded-lg bg-n-brand px-3 py-2 text-xs font-medium text-white disabled:opacity-50"
                type="button"
                :disabled="savingParticipants"
                @click="addParticipants"
              >
                {{
                  savingParticipants
                    ? $t('QUEPASA_INBOX.GROUPS.PANEL.ADD_LOADING')
                    : $t('QUEPASA_INBOX.GROUPS.PANEL.ADD_ACTION')
                }}
              </button>
            </div>
          </div>

          <div class="mt-4">
            <input
              v-model="search"
              class="w-full rounded-xl border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12"
              :placeholder="$t('QUEPASA_INBOX.GROUPS.PANEL.SEARCH_PLACEHOLDER')"
              type="search"
            />
          </div>

          <div v-if="error" class="mt-3 text-xs text-ruby-11">
            {{ error }}
          </div>

          <div v-if="loading" class="mt-4 text-sm text-n-slate-11">
            {{ $t('QUEPASA_INBOX.GROUPS.PANEL.LOADING') }}
          </div>

          <ul
            v-else-if="filteredParticipants.length"
            class="mt-4 max-h-72 overflow-auto divide-y divide-n-weak rounded-lg border border-n-weak bg-n-solid-1"
          >
            <li
              v-for="participant in filteredParticipants"
              :key="participant.id"
              class="px-4 py-3"
            >
              <div class="flex items-start justify-between gap-3">
                <div class="min-w-0 flex-1">
                  <div class="flex items-start justify-between gap-3 min-w-0">
                    <p
                      class="min-w-0 flex-1 text-sm font-medium leading-tight text-n-slate-12"
                    >
                      <span class="block truncate">
                        {{ participant.displayName }}
                      </span>
                      <span
                        v-if="participant.mentionable"
                        class="mt-2 block w-fit rounded-full bg-n-alpha-black2 px-2 py-1 text-[11px] font-medium text-n-slate-12"
                      >
                        {{ participant.mentionToken }}
                      </span>
                    </p>
                    <button
                      class="shrink-0 rounded-lg border border-n-weak bg-n-alpha-black2 px-2 py-1 text-xs font-medium text-n-slate-12 disabled:opacity-50"
                      type="button"
                      :disabled="!participant.mentionable"
                      @click="mentionParticipant(participant)"
                    >
                      {{ $t('QUEPASA_INBOX.GROUPS.PANEL.MENTION') }}
                    </button>
                  </div>
                  <div
                    v-if="participant.isSuperAdmin || participant.isAdmin"
                    class="mt-2"
                  >
                    <span
                      v-if="participant.isSuperAdmin"
                      class="text-[11px] font-medium text-emerald-11"
                    >
                      {{ $t('QUEPASA_INBOX.GROUPS.PANEL.SUPER_ADMIN') }}
                    </span>
                    <span
                      v-else-if="participant.isAdmin"
                      class="text-[11px] font-medium text-amber-11"
                    >
                      {{ $t('QUEPASA_INBOX.GROUPS.PANEL.ADMIN') }}
                    </span>
                  </div>
                </div>
              </div>
            </li>
          </ul>

          <div v-else class="mt-4 text-sm text-n-slate-11">
            {{ $t('QUEPASA_INBOX.GROUPS.PANEL.EMPTY') }}
          </div>
        </div>
      </AccordionItem>
    </div>
  </div>
</template>
